import { emptyFragment, Modal } from '@execonline-inc/execonline-ui';
import { OpenableAction } from '@execonline-inc/execonline-ui/dist/hooks/useOpenable/Types';
import { always } from '@kofno/piper';
import { Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import { currentUserStore } from '../../../CurrentUserStore';
import { findLinkBy } from '../../../Links';
import { Link } from '../../../Resource/Types';
import { TPlainTextKey } from '../../../Translations';
import LCModalBody from './LCModalBody';
import AccessRestrictionMessage from './LCModalBody/AccessRestrictionMessage';
import LCModalHeader from './LCModalHeader';

interface Props {
  isOpen: boolean;
  onClose: OpenableAction;
  context?: 'Home' | 'Learning Collections';
}

const hasCoreLicense = (): Maybe<Link> =>
  findLinkBy({ rel: 'create-learning-collection-v2-ai' }, currentUserStore.links);

// v2-enabled orgs hide the classic `new-learning-collection` link by
// default (unless the org opts in via the show_builder_v1 feature switch),
// so counting only that link would leave v2-only users with the "no
// license" banner even when v2 is fully accessible. Either create link
// counts as "user can create a collection". The two intermediate bindings
// are annotated `Maybe<Link>` so `.orElse` can unify the two link-rel
// literal types under the generic `Link` in the return.
const hasCreateLearningCollectionPrivilege = (): Maybe<Link> => {
  const v1LinkM: Maybe<Link> = findLinkBy(
    { rel: 'new-learning-collection' },
    currentUserStore.links,
  );
  const v2LinkM: Maybe<Link> = findLinkBy(
    { rel: 'new-learning-collection-template-v2' },
    currentUserStore.links,
  );
  return v1LinkM.orElse(() => v2LinkM);
};

const hasLicenseOrPrivilege = () =>
  hasCoreLicense().orElse(() => hasCreateLearningCollectionPrivilege());

const hasLicenseAndPrivilege = () =>
  hasCoreLicense().andThen(() => hasCreateLearningCollectionPrivilege());

const messageContent: TPlainTextKey = findLinkBy(
  { rel: 'create-learning-collection-v2-ai' },
  currentUserStore.links,
)
  .map<TPlainTextKey>(
    always(
      'Your admin is not permitted to build learning collections. Builder access is required to continue.',
    ),
  )
  .getOrElseValue(
    'There are no license available for your organization. You must have a license to continue.',
  );

function LCModal({ isOpen, onClose, context = 'Home' }: Props) {
  return (
    <Modal size="4xl" open={isOpen} onClose={onClose} className="flex flex-col">
      <LCModalHeader />
      {hasLicenseOrPrivilege()
        .map(() =>
          hasLicenseAndPrivilege()
            .map(emptyFragment)
            .getOrElse(() => <AccessRestrictionMessage message={messageContent} />),
        )
        .getOrElse(() => (
          <AccessRestrictionMessage message="You must have an organization license and builder access to continue. Contact ExecOnline for assistance." />
        ))}
      <LCModalBody context={context} />
    </Modal>
  );
}

export default observer(LCModal);
